import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiKeysAPI } from '../../services/api';
import toast from 'react-hot-toast';
import {
  HiOutlineKey, HiOutlinePlus, HiOutlineTrash, HiOutlineRefresh,
  HiOutlineEye, HiOutlineEyeOff, HiOutlineClipboardCopy, HiOutlineCheck,
  HiOutlineExclamation, HiOutlineShieldCheck, HiOutlineClock
} from 'react-icons/hi';

// Styles injected once for the API keys section
const sectionStyles = `
  .api-keys-section {
    margin-top: 0;
  }
  .api-keys-section h4 {
    border-bottom: none !important;
    padding-bottom: 0 !important;
    margin-bottom: 0 !important;
  }
`;

// Available API scopes with labels
const API_SCOPES = [
  { value: 'leads.read', label: 'Leads — Read', group: 'Leads' },
  { value: 'leads.write', label: 'Leads — Write', group: 'Leads' },
  { value: 'leads.delete', label: 'Leads — Delete', group: 'Leads' },
  { value: 'reports.read', label: 'Reports — Read', group: 'Reports' },
  { value: 'reports.export', label: 'Reports — Export', group: 'Reports' },
  { value: 'reports.daily-work', label: 'Daily Work — Read', group: 'Reports' },
  { value: 'reports.leaderboard', label: 'Leaderboard — Read', group: 'Reports' },
  { value: 'pipeline.read', label: 'Pipeline — Read', group: 'Pipeline' },
  { value: 'users.read', label: 'Users — Read', group: 'Users' },
  { value: 'users.write', label: 'Users — Write', group: 'Users' },
  { value: 'attendance.read', label: 'Attendance — Read', group: 'Attendance' },
  { value: 'call-logs.read', label: 'Call Logs — Read', group: 'Call Logs' },
  { value: 'recordings.read', label: 'Recordings — Read', group: 'Recordings' },
  { value: 'follow-ups.read', label: 'Follow-ups — Read', group: 'Follow-ups' },
  { value: 'follow-ups.write', label: 'Follow-ups — Write', group: 'Follow-ups' },
  { value: 'products.read', label: 'Products — Read', group: 'Products' },
  { value: 'products.write', label: 'Products — Write', group: 'Products' },
  { value: 'scripts.read', label: 'Scripts — Read', group: 'Scripts' },
  { value: 'scripts.write', label: 'Scripts — Write', group: 'Scripts' },
];

// Scope badge colors
const SCOPE_COLORS = {
  'leads.read': 'bg-blue-100 text-blue-700',
  'leads.write': 'bg-blue-200 text-blue-800',
  'leads.delete': 'bg-red-100 text-red-700',
  'reports.read': 'bg-purple-100 text-purple-700',
  'reports.export': 'bg-purple-200 text-purple-800',
  'reports.daily-work': 'bg-purple-100 text-purple-600',
  'reports.leaderboard': 'bg-purple-200 text-purple-700',
  'pipeline.read': 'bg-yellow-100 text-yellow-700',
  'users.read': 'bg-indigo-100 text-indigo-700',
  'users.write': 'bg-indigo-200 text-indigo-800',
  'attendance.read': 'bg-orange-100 text-orange-700',
  'call-logs.read': 'bg-cyan-100 text-cyan-700',
  'recordings.read': 'bg-teal-100 text-teal-700',
  'follow-ups.read': 'bg-amber-100 text-amber-700',
  'follow-ups.write': 'bg-amber-200 text-amber-800',
  'products.read': 'bg-emerald-100 text-emerald-700',
  'products.write': 'bg-emerald-200 text-emerald-800',
  'scripts.read': 'bg-violet-100 text-violet-700',
  'scripts.write': 'bg-violet-200 text-violet-800',
};

const SCOPE_LABELS = Object.fromEntries(API_SCOPES.map(s => [s.value, s.label]));

// Create API Key Modal
const CreateApiKeyModal = ({ isOpen, onClose, orgId, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    scopes: ['leads.read'],
    rateLimit: 1000,
    allowedIps: '',
    expiresAt: ''
  });
  const [formError, setFormError] = useState('');

  const handleScopeToggle = (scope) => {
    setFormData(prev => ({
      ...prev,
      scopes: prev.scopes.includes(scope)
        ? prev.scopes.filter(s => s !== scope)
        : [...prev.scopes, scope]
    }));
  };

  const handleGroupToggle = (group, scopes) => {
    const groupValues = scopes.map(s => s.value);
    const allSelected = groupValues.every(v => formData.scopes.includes(v));
    if (allSelected) {
      // Deselect all in this group
      setFormData(prev => ({
        ...prev,
        scopes: prev.scopes.filter(s => !groupValues.includes(s))
      }));
    } else {
      // Select all in this group
      setFormData(prev => ({
        ...prev,
        scopes: [...new Set([...prev.scopes, ...groupValues])]
      }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      setFormError('Name is required');
      return;
    }
    if (formData.scopes.length === 0) {
      setFormError('At least one scope is required');
      return;
    }
    setFormError('');
    onSave({
      ...formData,
      allowedIps: formData.allowedIps
        ? formData.allowedIps.split(',').map(ip => ip.trim()).filter(Boolean)
        : [],
      expiresAt: formData.expiresAt || null
    });
  };

  if (!isOpen) return null;

  // Group scopes
  const groupedScopes = {};
  API_SCOPES.forEach(scope => {
    if (!groupedScopes[scope.group]) groupedScopes[scope.group] = [];
    groupedScopes[scope.group].push(scope);
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-primary-100 flex items-center justify-center">
              <HiOutlineKey className="w-5 h-5 text-primary-600" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900">Create API Key</h2>
              <p className="text-xs text-gray-500 mt-0.5">
                The API key and signing secret will be shown only once after creation.
              </p>
            </div>
          </div>
        </div>

        {/* Scrollable form body */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="px-6 py-5 space-y-5">

            {/* Row: Name + Rate Limit */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                  placeholder="e.g., Production API Key"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Rate Limit (req/hr)
                </label>
                <input
                  type="number"
                  value={formData.rateLimit}
                  onChange={(e) => setFormData(prev => ({ ...prev, rateLimit: parseInt(e.target.value) || 0 }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                  min={0}
                  placeholder="1000"
                />
                <p className="text-xs text-gray-400 mt-0.5">0 = unlimited</p>
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none resize-none"
                rows={2}
                placeholder="What is this key used for?"
              />
            </div>

            {/* Scopes / Permissions */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-gray-700">
                  Scopes / Permissions <span className="text-red-500">*</span>
                </label>
                <span className="text-xs text-gray-400">
                  {formData.scopes.length} of {API_SCOPES.length} selected
                </span>
              </div>
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                {Object.entries(groupedScopes).map(([group, scopes], groupIdx) => {
                  const allSelected = scopes.every(s => formData.scopes.includes(s.value));
                  return (
                    <div key={group} className={`${groupIdx > 0 ? 'border-t border-gray-200' : ''}`}>
                      {/* Group header with select-all toggle */}
                      <div
                        className="flex items-center justify-between px-4 py-2 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors"
                        onClick={() => handleGroupToggle(group, scopes)}
                      >
                        <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">{group}</span>
                        <span className={`text-xs font-medium px-2 py-0.5 rounded ${
                          allSelected
                            ? 'bg-primary-100 text-primary-700'
                            : 'text-gray-400'
                        }`}>
                          {allSelected ? 'All selected' : `${scopes.filter(s => formData.scopes.includes(s.value)).length}/${scopes.length}`}
                        </span>
                      </div>
                      {/* Scope checkboxes in a grid */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-0">
                        {scopes.map(scope => {
                          const isSelected = formData.scopes.includes(scope.value);
                          return (
                            <label
                              key={scope.value}
                              className={`flex items-center gap-2.5 px-4 py-2 cursor-pointer transition-colors ${
                                isSelected ? 'bg-primary-50' : 'hover:bg-gray-50'
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => handleScopeToggle(scope.value)}
                                className="h-3.5 w-3.5 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                              />
                              <span className={`text-sm ${isSelected ? 'text-gray-900 font-medium' : 'text-gray-600'}`}>
                                {scope.label}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Row: Allowed IPs + Expiration */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Allowed IPs <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <input
                  type="text"
                  value={formData.allowedIps}
                  onChange={(e) => setFormData(prev => ({ ...prev, allowedIps: e.target.value }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                  placeholder="e.g., 192.168.1.1, 10.0.0.1"
                />
                <p className="text-xs text-gray-400 mt-0.5">Comma-separated. Leave empty to allow all.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Expiration <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <input
                  type="date"
                  value={formData.expiresAt}
                  onChange={(e) => setFormData(prev => ({ ...prev, expiresAt: e.target.value }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                />
                <p className="text-xs text-gray-400 mt-0.5">Leave empty for no expiration.</p>
              </div>
            </div>
          </div>

          {/* Error */}
          {formError && (
            <div className="px-6 pb-3">
              <p className="text-sm text-red-600 font-medium bg-red-50 px-3 py-2 rounded-md">{formError}</p>
            </div>
          )}

          {/* Footer buttons */}
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end gap-3 flex-shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 transition-colors shadow-sm"
            >
              Create API Key
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Main Component
const OrganizationsApiKeys = ({ orgId }) => {
  const queryClient = useQueryClient();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createdKey, setCreatedKey] = useState(null); // { apiKey, secretKey }
  const [revealedSecret, setRevealedSecret] = useState(null); // { keyId, secretKey }
  const [regeneratingKey, setRegeneratingKey] = useState(null);
  const [deleteKey, setDeleteKey] = useState(null);
  const [copiedField, setCopiedField] = useState('');

  // Fetch API keys
  const { data: keysData, isLoading } = useQuery({
    queryKey: ['apiKeys', orgId],
    queryFn: async () => {
      const response = await apiKeysAPI.list(orgId);
      return response.data;
    },
    enabled: !!orgId
  });

  const apiKeys = keysData?.data || [];

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data) => apiKeysAPI.create(orgId, data),
    onSuccess: (response) => {
      queryClient.invalidateQueries(['apiKeys', orgId]);
      setCreatedKey({
        apiKey: response.data?.data?.apiKey,
        secretKey: response.data?.data?.secretKey
      });
      setShowCreateModal(false);
      toast.success('API key created! Copy the key and secret now — they will not be shown again.');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to create API key');
    }
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (keyId) => apiKeysAPI.delete(orgId, keyId),
    onSuccess: () => {
      queryClient.invalidateQueries(['apiKeys', orgId]);
      setDeleteKey(null);
      toast.success('API key deleted');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to delete API key');
    }
  });

  // Toggle active mutation
  const toggleMutation = useMutation({
    mutationFn: ({ keyId, isActive }) => apiKeysAPI.update(orgId, keyId, { isActive }),
    onSuccess: () => {
      queryClient.invalidateQueries(['apiKeys', orgId]);
      toast.success('API key updated');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to update API key');
    }
  });

  // Reveal secret mutation
  const revealMutation = useMutation({
    mutationFn: (keyId) => apiKeysAPI.revealSecret(orgId, keyId),
    onSuccess: (response) => {
      setRevealedSecret({
        keyId: response.data?.data?._id,
        secretKey: response.data?.data?.secretKey
      });
      queryClient.invalidateQueries(['apiKeys', orgId]);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to reveal secret');
    }
  });

  // Regenerate secret mutation
  const regenerateSecretMutation = useMutation({
    mutationFn: (keyId) => apiKeysAPI.regenerateSecret(orgId, keyId),
    onSuccess: (response) => {
      setRevealedSecret({
        keyId: response.data?.data?._id,
        secretKey: response.data?.data?.secretKey
      });
      queryClient.invalidateQueries(['apiKeys', orgId]);
      toast.success('New signing secret generated. Copy it now — it will only be shown once.');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to regenerate secret');
    }
  });

  // Regenerate key mutation
  const regenerateKeyMutation = useMutation({
    mutationFn: (keyId) => apiKeysAPI.regenerateKey(orgId, keyId),
    onSuccess: (response) => {
      setRegeneratingKey(null);
      queryClient.invalidateQueries(['apiKeys', orgId]);
      setCreatedKey({
        apiKey: response.data?.data?.apiKey,
        secretKey: null // New key doesn't reveal the secret
      });
      toast.success('New API key generated! Copy it now — the old key is no longer valid.');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to regenerate key');
    }
  });

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopiedField(label);
    setTimeout(() => setCopiedField(''), 2000);
  };

  if (!orgId) return null;

  return (
    <div className="api-keys-section">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="min-w-0">
          <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
            <HiOutlineKey className="w-4 h-4 text-primary-600 flex-shrink-0" />
            API Access
          </h4>
          <p className="text-xs text-gray-500 mt-0.5">
            Manage API keys for programmatic access. Keys are scoped to specific permissions.
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 transition-colors shadow-sm whitespace-nowrap flex-shrink-0"
        >
          <HiOutlinePlus className="w-4 h-4" />
          Create Key
        </button>
      </div>

      {/* Newly Created Key Banner */}
      {createdKey && (
        <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
              <HiOutlineShieldCheck className="w-4 h-4 text-green-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-green-800 mb-2">
                API Key Created — Copy these now!
              </p>

              {createdKey.apiKey && (
                <div className="mb-2">
                  <p className="text-xs font-medium text-green-700 mb-1">API Key</p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 text-xs bg-white border border-green-200 px-3 py-2 rounded font-mono break-all text-gray-900 min-w-0">
                      {createdKey.apiKey}
                    </code>
                    <button
                      onClick={() => copyToClipboard(createdKey.apiKey, 'apiKey')}
                      className="p-2 text-green-600 hover:text-green-800 hover:bg-green-100 rounded transition-colors flex-shrink-0"
                      title="Copy API key"
                    >
                      {copiedField === 'apiKey' ? (
                        <HiOutlineCheck className="w-4 h-4" />
                      ) : (
                        <HiOutlineClipboardCopy className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>
              )}

              {createdKey.secretKey && (
                <div className="mb-2">
                  <p className="text-xs font-medium text-green-700 mb-1">Signing Secret</p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 text-xs bg-white border border-green-200 px-3 py-2 rounded font-mono break-all text-gray-900 min-w-0">
                      {createdKey.secretKey}
                    </code>
                    <button
                      onClick={() => copyToClipboard(createdKey.secretKey, 'secretKey')}
                      className="p-2 text-green-600 hover:text-green-800 hover:bg-green-100 rounded transition-colors flex-shrink-0"
                      title="Copy signing secret"
                    >
                      {copiedField === 'secretKey' ? (
                        <HiOutlineCheck className="w-4 h-4" />
                      ) : (
                        <HiOutlineClipboardCopy className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>
              )}

              <button
                onClick={() => setCreatedKey(null)}
                className="mt-1 text-xs text-green-700 hover:text-green-900 font-medium underline underline-offset-2"
              >
                I've copied them, dismiss this
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Revealed Secret Banner */}
      {revealedSecret && (
        <div className="mb-4 bg-amber-50 border border-amber-300 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0 mt-0.5">
              <HiOutlineEye className="w-4 h-4 text-amber-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-amber-800 mb-1.5">
                ⚠ Signing Secret — Copy it now, you won't be able to see it again!
              </p>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-xs bg-white border border-amber-200 px-3 py-2 rounded font-mono break-all text-gray-900 min-w-0">
                  {revealedSecret.secretKey}
                </code>
                <button
                  onClick={() => copyToClipboard(revealedSecret.secretKey, 'revealedSecret')}
                  className="p-2 text-amber-700 hover:text-amber-900 hover:bg-amber-100 rounded transition-colors flex-shrink-0"
                  title="Copy secret"
                >
                  {copiedField === 'revealedSecret' ? (
                    <HiOutlineCheck className="w-4 h-4" />
                  ) : (
                    <HiOutlineClipboardCopy className="w-4 h-4" />
                  )}
                </button>
              </div>
              <button
                onClick={() => { setRevealedSecret(null); queryClient.invalidateQueries(['apiKeys', orgId]); }}
                className="mt-2 text-xs text-amber-700 hover:text-amber-900 font-medium underline underline-offset-2"
              >
                I've copied it, hide this secret
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Loading */}
      {isLoading ? (
        <div className="flex items-center justify-center py-10 text-gray-400">
          <svg className="animate-spin w-5 h-5 mr-2" viewBox="0 0 24 24" fill="none">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          Loading API keys...
        </div>
      ) : apiKeys.length === 0 ? (
        <div className="text-center py-10 bg-gray-50 rounded-lg border border-dashed border-gray-200">
          <HiOutlineKey className="w-10 h-10 text-gray-300 mx-auto mb-2" />
          <p className="text-sm font-medium text-gray-500">No API keys yet</p>
          <p className="text-xs text-gray-400 mt-1">Create an API key to allow programmatic access to this organization's data.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {apiKeys.map(key => (
            <div key={key._id} className="border border-gray-200 rounded-lg hover:border-gray-300 transition-colors overflow-hidden">
              {/* Card Header */}
              <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200">
                <div className="flex items-center gap-2 min-w-0">
                  <HiOutlineKey className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <h5 className="text-sm font-semibold text-gray-900 truncate">{key.name}</h5>
                  {key.description && (
                    <span className="text-xs text-gray-500 truncate hidden sm:inline">— {key.description}</span>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${
                    key.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                  }`}>
                    {key.isActive ? 'Active' : 'Disabled'}
                  </span>
                  {key.expiresAt && new Date(key.expiresAt) < new Date() && (
                    <span className="text-xs px-2.5 py-1 rounded-full bg-red-100 text-red-700 font-medium">
                      Expired
                    </span>
                  )}
                </div>
              </div>

              {/* Card Body */}
              <div className="px-4 py-3 space-y-3">
                {/* Key Prefix & Secret */}
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-gray-500">Key:</span>
                    <code className="text-xs bg-gray-100 px-2 py-0.5 rounded font-mono text-gray-700">{key.keyPrefix}</code>
                  </div>

                  {/* Secret reveal/regenerate */}
                  {key.secretRevealed ? (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-400 italic">Secret: Already viewed</span>
                      <button
                        onClick={() => regenerateSecretMutation.mutate(key._id)}
                        className="text-xs text-primary-600 hover:text-primary-700 font-medium hover:underline underline-offset-2"
                        disabled={regenerateSecretMutation.isPending}
                      >
                        Regenerate Secret
                      </button>
                    </div>
                  ) : key.secretRevealedAt ? (
                    <div className="flex items-center gap-2">
                      <code className="text-xs bg-gray-100 px-2 py-0.5 rounded text-gray-400 font-mono">••••••••</code>
                      <button
                        onClick={() => regenerateSecretMutation.mutate(key._id)}
                        className="text-xs text-primary-600 hover:text-primary-700 font-medium hover:underline underline-offset-2"
                        disabled={regenerateSecretMutation.isPending}
                      >
                        Regenerate Secret
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1.5">
                      <code className="text-xs bg-gray-100 px-2 py-0.5 rounded font-mono text-gray-400">••••••••</code>
                      <button
                        onClick={() => revealMutation.mutate(key._id)}
                        className="p-1 text-gray-400 hover:text-primary-600 rounded transition-colors"
                        title="Reveal secret (one-time)"
                        disabled={revealMutation.isPending}
                      >
                        <HiOutlineEye className="w-4 h-4" />
                      </button>
                      <span className="text-xs text-gray-400">Reveal once</span>
                    </div>
                  )}
                </div>

                {/* Scopes */}
                <div className="flex flex-wrap gap-1">
                  {key.scopes?.map(scope => (
                    <span
                      key={scope}
                      className={`text-xs px-2 py-0.5 rounded font-medium ${SCOPE_COLORS[scope] || 'bg-gray-100 text-gray-600'}`}
                    >
                      {SCOPE_LABELS[scope] || scope}
                    </span>
                  ))}
                </div>

                {/* Meta info */}
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500">
                  {key.rateLimit > 0 && (
                    <span className="flex items-center gap-1">
                      <HiOutlineClock className="w-3.5 h-3.5" />
                      {key.rateLimit} req/hr
                    </span>
                  )}
                  {key.totalRequests > 0 && (
                    <span>{key.totalRequests.toLocaleString()} requests</span>
                  )}
                  {key.lastUsedAt && (
                    <span>Last used {new Date(key.lastUsedAt).toLocaleDateString()}</span>
                  )}
                  {key.expiresAt && (
                    <span>Expires {new Date(key.expiresAt).toLocaleDateString()}</span>
                  )}
                </div>
              </div>

              {/* Card Actions */}
              <div className="flex items-center gap-2 px-4 py-2.5 bg-gray-50 border-t border-gray-200">
                <button
                  onClick={() => toggleMutation.mutate({ keyId: key._id, isActive: !key.isActive })}
                  className={`text-xs px-3 py-1.5 rounded-md font-medium transition-colors ${
                    key.isActive
                      ? 'text-yellow-700 bg-yellow-50 border border-yellow-200 hover:bg-yellow-100'
                      : 'text-green-700 bg-green-50 border border-green-200 hover:bg-green-100'
                  }`}
                  disabled={toggleMutation.isPending}
                >
                  {key.isActive ? 'Disable' : 'Enable'}
                </button>
                <button
                  onClick={() => setRegeneratingKey(key._id)}
                  className="text-xs px-3 py-1.5 rounded-md font-medium text-gray-600 bg-white border border-gray-200 hover:bg-gray-50 transition-colors"
                >
                  <HiOutlineRefresh className="w-3.5 h-3.5 inline -mt-0.5 mr-1" />
                  New Key
                </button>
                <button
                  onClick={() => setDeleteKey(key)}
                  className="text-xs px-3 py-1.5 rounded-md font-medium text-red-600 bg-white border border-red-200 hover:bg-red-50 transition-colors flex items-center gap-1"
                >
                  <HiOutlineTrash className="w-3.5 h-3.5" />
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Regenerate Key Confirmation */}
      {regeneratingKey && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="p-6">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                  <HiOutlineExclamation className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <h4 className="text-base font-semibold text-gray-900">Regenerate API Key?</h4>
                  <p className="text-sm text-gray-500 mt-1">
                    The current API key will immediately stop working. Any applications using it will need to be updated with the new key.
                  </p>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 bg-gray-50 border-t border-gray-200">
              <button
                onClick={() => setRegeneratingKey(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => regenerateKeyMutation.mutate(regeneratingKey)}
                disabled={regenerateKeyMutation.isPending}
                className="px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-md hover:bg-amber-700 transition-colors disabled:opacity-50"
              >
                {regenerateKeyMutation.isPending ? 'Regenerating...' : 'Yes, regenerate'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {deleteKey && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="p-6">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                  <HiOutlineExclamation className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <h4 className="text-base font-semibold text-gray-900">Delete API Key?</h4>
                  <p className="text-sm text-gray-500 mt-1">
                    This will permanently delete the key <strong>{deleteKey.name}</strong> ({deleteKey.keyPrefix}). Any applications using this key will immediately lose access.
                  </p>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 bg-gray-50 border-t border-gray-200">
              <button
                onClick={() => setDeleteKey(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => deleteMutation.mutate(deleteKey._id)}
                disabled={deleteMutation.isPending}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {deleteMutation.isPending ? 'Deleting...' : 'Delete Key'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Modal */}
      <CreateApiKeyModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        orgId={orgId}
        onSave={(data) => createMutation.mutate(data)}
      />

      <style>{sectionStyles}</style>
    </div>
  );
};

export default OrganizationsApiKeys;